import React, { useState, useEffect } from 'react';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

const COLS = [
  { key: 'shacharit', label: 'שחרית', accent: '#1e3a5f' },
  { key: 'mincha',    label: 'מנחה',  accent: '#b8860b' },
  { key: 'maariv',   label: 'מעריב', accent: '#374151' },
];

const todayDow = new Date().getDay();
const todayHebrewDay = (() => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
})();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) =>
  (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8', backgroundColor: '#fafafa' }}>
    {msg}
  </div>
);

const Columns = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <StatusBox msg="טוען..." />;
  if (error) return <StatusBox msg={error} isError />;
  if (!processedCustomTimes?.length) return <StatusBox msg="אין זמני תפילה" />;

  const nowMin = now.getHours() * 60 + now.getMinutes();

  // Group by prayer category
  const byKey = {};
  for (const col of COLS) byKey[col.key] = [];
  for (const ct of [...processedCustomTimes].sort((a, b) =>
    PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
  )) {
    if (byKey[ct.prayer] !== undefined) byKey[ct.prayer].push(ct);
  }

  // Find global next entry: { colKey, itemIdx }
  let nextColKey = null, nextItemIdx = -1;
  if (highlightNextMinyan) {
    let bestMin = Infinity;
    for (const col of COLS) {
      (byKey[col.key] || []).forEach((ct, i) => {
        if (ct.isActiveToday === false) return;
        const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days));
        const times = ct.type === 'recurring'
          ? (activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0))
          : (() => { const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime); return m >= 0 ? [m] : []; })();
        for (const m of times) {
          if (m >= nowMin && m < bestMin) { bestMin = m; nextColKey = col.key; nextItemIdx = i; }
        }
      });
    }
  }

  const activeCols = COLS.filter(col => byKey[col.key].length > 0);

  return (
    <div style={{ width: '100%', height: '100%', direction: 'rtl', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', boxSizing: 'border-box', overflow: 'hidden' }}>
      {showTitle && (
        <div style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e3a5f', letterSpacing: '0.01em' }}>{title}</span>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {activeCols.map((col, colIdx) => {
          const items = byKey[col.key];
          const isLast = colIdx === activeCols.length - 1;

          return (
            <div key={col.key} style={{
              flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
              borderLeft: isLast ? 'none' : '1px solid #f1f5f9',
            }}>
              {/* Column header */}
              <div style={{
                padding: '0.625rem 1rem',
                borderBottom: `2px solid ${col.accent}`,
                flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: col.accent, flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem', fontWeight: 700, color: col.accent, letterSpacing: '0.04em' }}>{col.label}</span>
              </div>

              {/* Items */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '0.5rem 0' }}>
                {items.map((ct, idx) => {
                  const isRecurring = ct.type === 'recurring';
                  const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days));
                  const isLast = idx === items.length - 1;
                  const isNext = highlightNextMinyan && col.key === nextColKey && idx === nextItemIdx;

                  const mainTime = activeOverride
                    ? activeOverride.resolvedTime
                    : isRecurring
                      ? (ct.resolvedSlots?.length ? ct.resolvedSlots[0] : null)
                      : ct.resolvedTime;

                  return (
                    <div key={idx} style={{
                      padding: '0.625rem 1rem',
                      borderBottom: isLast ? 'none' : '1px solid #f8fafc',
                      display: 'flex', flexDirection: 'column', gap: '3px',
                      backgroundColor: isNext ? '#eff6ff' : 'transparent',
                      borderRight: isNext ? `3px solid ${col.accent}` : 'none',
                      borderLeft: isNext ? '1px solid rgba(30,58,95,0.15)' : 'none',
                    }}>
                      {/* Sub-label (if item has a custom label different from category) */}
                      {ct.label && (
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em' }}>
                          {ct.label}
                        </span>
                      )}

                      {/* Time row */}
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                        <span style={{
                          fontSize: '1.625rem',
                          fontWeight: 800,
                          color: activeOverride ? '#166534' : '#0f172a',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontVariantNumeric: 'tabular-nums lining-nums',
                          direction: 'ltr',
                          lineHeight: 1,
                          letterSpacing: '-0.03em',
                        }}>
                          {mainTime || '—'}
                        </span>
                        {isRecurring && (
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#166534', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '99px', padding: '1px 6px', flexShrink: 0 }}>
                            כל {ct.interval} דק׳
                          </span>
                        )}
                      </div>

                      {/* Time rule / days */}
                      {(!isRecurring && formatTimeRule(ct) || ct.daysLabel) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 4px', alignItems: 'baseline' }}>
                          {!isRecurring && formatTimeRule(ct) && (
                            <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                              {formatTimeRule(ct)}{ct.daysLabel ? ' ·' : ''}
                            </span>
                          )}
                          {ct.daysLabel && (
                            <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                              {ct.daysLabel}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Recurring range */}
                      {isRecurring && !activeOverride && ct.resolvedSlots?.length > 1 && (
                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>
                          עד {ct.resolvedSlots[ct.resolvedSlots.length - 1]}
                        </span>
                      )}

                      {/* Recurring full slots */}
                      {isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', direction: 'ltr', marginTop: '2px' }}>
                          {ct.resolvedSlots.map((slot, i) => (
                            <span key={i} style={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b', fontVariantNumeric: 'tabular-nums', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '3px', padding: '1px 4px' }}>
                              {slot}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Day overrides */}
                      {(ct.resolvedOverrides || []).map((ov, oi) => {
                        const active = isOverrideActive(ov.days);
                        const dayLabel = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                        return (
                          <div key={oi} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: active ? '2px 6px' : '1px 2px',
                            borderRadius: '5px',
                            backgroundColor: active ? '#f0fdf4' : 'transparent',
                            border: active ? '1px solid #bbf7d0' : 'none',
                          }}>
                            <span style={{ fontSize: '0.7rem', color: active ? '#15803d' : '#cbd5e1', fontWeight: active ? 700 : 400 }}>{dayLabel}</span>
                            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: active ? '#15803d' : '#cbd5e1', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{ov.resolvedTime}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Columns;
